import {
  ActionButton,
  ActionFooter,
  Caption,
  type ListNavigation,
  SelectionList,
  SelectionRow,
  Shortcut,
  SubsectionHeader,
  useShortcut,
} from '@if/mui'
import { Card, CardContent, Divider, Stack, Tooltip } from '@mui/material'
import { InteracWireSearchPossibleMatchFilters } from '@src/api/interac-wire-api'
import { EInteracWireStatus, ESystemMatchingResult, InteracPossibleMatch, InteracWire } from '@src/types/interac'
import { Ban, Link2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import InteracCandidateRow from './InteracCandidateRow'
import InteracCandidateSearchFilters from './InteracCandidateSearchFilters'

const NO_MATCH_KEY = { key: 'n' }
const DISCARD_KEY = { key: 'd' }

interface InteracCandidatesCardProps {
  wire: InteracWire
  candidates: InteracPossibleMatch[]
  nav: ListNavigation
  isSearching: boolean
  searchFilters: InteracWireSearchPossibleMatchFilters | null
  onSearchChange: (filters: InteracWireSearchPossibleMatchFilters | null) => void
  selectedLoanId: string | null
  keysEnabled: boolean
  onPick: (index: number) => void
  onMatch: () => void
  onNoMatch: () => void
  onDiscard: () => void
  onOpenNotes: (candidate: InteracPossibleMatch) => void
}

const InteracCandidatesCard = ({
  wire,
  candidates,
  nav,
  isSearching,
  searchFilters,
  onSearchChange,
  selectedLoanId,
  keysEnabled,
  onPick,
  onMatch,
  onNoMatch,
  onDiscard,
  onOpenNotes,
}: InteracCandidatesCardProps) => {
  const { t } = useTranslation()

  const searchQuery = searchFilters?.referenceNumber ?? searchFilters?.email ?? null
  const alreadyProcessed = wire.status === EInteracWireStatus.InProcess
  const alreadyNoMatch = wire.systemMatchingResult === ESystemMatchingResult.None

  const matchReason = alreadyProcessed
    ? t('interacWire.actions.matchDisabledInProcess')
    : !selectedLoanId
      ? t('interacWire.actions.matchDisabledNoSelection')
      : undefined

  const noMatchReason = alreadyProcessed
    ? t('interacWire.actions.noMatchDisabledInProcess')
    : alreadyNoMatch
      ? t('interacWire.actions.noMatchDisabledAlready')
      : undefined

  useShortcut(NO_MATCH_KEY, onNoMatch, { enabled: keysEnabled && !noMatchReason })
  useShortcut(DISCARD_KEY, onDiscard, { enabled: keysEnabled })

  return (
    <Card>
      <SubsectionHeader
        icon={Link2}
        title={t('interacWire.candidates.title')}
        subtitle={
          searchQuery
            ? t('interacWire.candidates.found', { count: candidates.length, query: searchQuery })
            : t('interacWire.candidates.proposed', { count: candidates.length })
        }
        action={<Caption>{t('interacWire.candidates.anyLoan')}</Caption>}
      />
      <CardContent sx={{ pt: 0 }}>
        <InteracCandidateSearchFilters onChange={onSearchChange} loading={isSearching} />
      </CardContent>

      <Divider />

      {candidates.length === 0 ? (
        <CardContent>
          <Caption>{searchQuery ? t('interacWire.candidates.noResults') : t('interacWire.candidates.none')}</Caption>
        </CardContent>
      ) : (
        <SelectionList
          label={t('interacWire.candidates.listLabel')}
          tabStop={false}
          activeDescendant={nav.activeDescendant}
        >
          {candidates.map((candidate, index) => (
            <SelectionRow
              key={candidate.loanId}
              // Arrowing onto a row is choosing it, so the cursor and the pick are
              // the same state — `selected` alone, never a second neutral tint.
              selected={nav.isActive(index)}
              onClick={() => onPick(index)}
              {...nav.getItemProps(index)}
            >
              <InteracCandidateRow candidate={candidate} transferAmount={wire.amount} onOpenNotes={onOpenNotes} />
            </SelectionRow>
          ))}
        </SelectionList>
      )}

      <ActionFooter
        leading={
          <Stack direction="row" gap={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={0.75}>
              <Shortcut displayOnly forceDisplay shortcut={{ key: 'ArrowLeft' }} />
              <Shortcut displayOnly forceDisplay shortcut={{ key: 'ArrowRight' }} />
              <Caption>{t('interacWire.candidates.keyLoan')}</Caption>
            </Stack>

            <Stack direction="row" spacing={0.75}>
              <Shortcut displayOnly forceDisplay shortcut={{ key: 'Enter' }} />
              <Caption>{t('interacWire.candidates.keyMatch')}</Caption>
            </Stack>
            <Stack direction="row" spacing={0.75}>
              <Shortcut displayOnly forceDisplay shortcut={NO_MATCH_KEY} />
              <Caption>{t('interacWire.candidates.keyNoMatch')}</Caption>
            </Stack>
            <Stack direction="row" spacing={0.75}>
              <Shortcut displayOnly forceDisplay shortcut={DISCARD_KEY} />
              <Caption>{t('interacWire.candidates.keyDiscard')}</Caption>
            </Stack>
          </Stack>
        }
      >
        <ActionButton tone="destructive" startIcon={Trash2} onClick={onDiscard}>
          {t('interacWire.actions.discard')}
        </ActionButton>
        <Tooltip title={noMatchReason ?? ''}>
          <span>
            <ActionButton tone="warning" startIcon={Ban} disabled={Boolean(noMatchReason)} onClick={onNoMatch}>
              {t('interacWire.actions.noMatch')}
            </ActionButton>
          </span>
        </Tooltip>
        <Tooltip title={matchReason ?? ''}>
          <span>
            <ActionButton emphasis="principal" startIcon={Link2} disabled={Boolean(matchReason)} onClick={onMatch}>
              {t('interacWire.actions.match')}
            </ActionButton>
          </span>
        </Tooltip>
      </ActionFooter>
    </Card>
  )
}

export default InteracCandidatesCard
